import { getRootDomain } from '@/composables/useCookieDomain';

export interface CookieStorageOptions {
    maxAge: number;
    sameSite?: 'strict' | 'lax' | 'none';
    path?: string;
    secure?: boolean;
    domain?: string;
  }
//TODO: przeanalizować opcje cookiesów pod kątem zwiększenia bezpieczeństwa i potestować w praktyce np. flaga secure
export const DEFAULT_COOKIE_OPTIONS: CookieStorageOptions = {
  maxAge: 86400, // 24 hours
  sameSite: 'lax',
  path: '/',
} as const;

export const getCookie = (name: string): any => {
  const cookies = document.cookie.split(';').reduce((acc, cookie) => {
    const [cookieName, cookieValue,] = cookie.trim().split('=');
    if (cookieName === name && cookieValue) {
      try {
        acc = JSON.parse(decodeURIComponent(cookieValue));
      } catch (e) {
        console.error(`Error parsing cookie ${name}:`, e);
      }
    }
    return acc;
  }, {} as Record<string, any>);

  return Object.keys(cookies).length > 0 ? cookies : null;
};
/**
   * Creates a custom cookie storage implementation that includes specified keys
   * Using plain JavaScript to manage cookies, when using the useCookie composable it simply does not work here.
   * It looses reactivity, saves the cookie but does not update the ref.
   */
export const createCustomCookieStorage = (name: string, keysToInclude: string[]): Storage => {
  const setCookie = (name: string, value: any, options: CookieStorageOptions): void => {
    const serializedValue = encodeURIComponent(JSON.stringify(value));
    const expires = new Date(Date.now() + options.maxAge * 1000).toUTCString();
    const sameSite = options.sameSite || 'lax';
    const path = options.path || '/';
    const secure = options.secure ? 'secure' : '';
    const domain = options.domain ? `domain=${options.domain};` : '';

    document.cookie = `${name}=${serializedValue};expires=${expires};path=${path};samesite=${sameSite};${domain}${secure ? 'secure;' : ''}`;
  };

  const removeCookie = (name: string, options: Partial<CookieStorageOptions> = {}): void => {
    const path = options.path || '/';
    const domain = options.domain ? `domain=${options.domain};` : '';
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};${domain}`;
  };

  const filterKeys = (data: Record<string, unknown>) => {
    return Object.fromEntries(
      Object.entries(data)
        .filter(([key,]) => keysToInclude.includes(key))
    );
  };

  return {
    get length(): number {
      const storageData = getCookie(name) || {};
      return Object.keys(storageData).length;
    },

    key(index: number): string | null {
      const storageData = getCookie(name) || {};
      const key = Object.keys(storageData).at(index) ?? null;
      return key;
    },

    getItem(key: string): string | null {
      const storageData = getCookie(name) || {};

      try {
        const item = storageData[key];

        if (!item || typeof item !== 'object') {
          return null;
        }

        return JSON.stringify(item);
      } catch (error) {
        console.error('Error getting item from cookie storage:', error);
        return null;
      }
    },

    setItem(key: string, value: string): void {
      try {
        // Safely parse the JSON string
        let data: Record<string, unknown>;
        try {
          data = JSON.parse(value);
        } catch (parseError) {
          console.error('Failed to parse value as JSON:', parseError);
          return;
        }

        if (!data || typeof data !== 'object' || Array.isArray(data)) {
          console.error('Invalid data format (not an object), skipping setItem');
          return;
        }
        const filtered = filterKeys(data);

        if (Object.keys(filtered).length) {
          const currentValue = getCookie(name) || {};
          const newValue = {
            ...currentValue,
            [key]: filtered,
          };
          setCookie(name, newValue, { ...DEFAULT_COOKIE_OPTIONS, domain: getRootDomain(), });
        } else {
          console.log('No valid keys to set after filtering');
        }
      } catch (error) {
        console.error('Error setting item in cookie storage:', error);
      }
    },

    removeItem(key: string): void {
      const currentValue = getCookie(name) || {};
      if (!currentValue || !Object.keys(currentValue).length) {
        return;
      }
      const { [key]: _, ...rest } = currentValue;
      setCookie(name, rest, { ...DEFAULT_COOKIE_OPTIONS, domain: getRootDomain(), });
    },

    clear(): void {
      removeCookie(name);
    },
  };
};
